/**
 * AI Career Counselor - Comprehensive AI-Powered Career Guidance
 * Uses ALL youth data: interests, skills, goals, activities, check-ins
 * Provides personalized career pathways and action plans
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, Brain, Target, TrendingUp, Lightbulb, 
  CheckCircle, ArrowRight, Loader2, MessageCircle, Send, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Navigation from '@/components/Navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AICareerCounselor = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [profileAnalysis, setProfileAnalysis] = useState(null);
  const [careerSuggestions, setCareerSuggestions] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(`ai_chat_${user.id}_${Date.now()}`);
  
  // Youth profile data
  const [interests, setInterests] = useState([]);
  const [skills, setSkills] = useState([]);
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    fetchProfileData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [interestsRes, skillsRes, goalsRes] = await Promise.all([
        fetch(`${API_URL}/api/profile/interests`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/profile/skills`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/goals`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      if (interestsRes.ok) setInterests((await interestsRes.json()).interests || []);
      if (skillsRes.ok) setSkills((await skillsRes.json()).skills || []);
      if (goalsRes.ok) setGoals(await goalsRes.json());
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/career-guidance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          interests,
          skills: skills.map(s => s.skill_id),
          education_level: 'high_school'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setCareerSuggestions(data.careers || []);
        toast.success('AI analyzed your profile! ✨');
      }
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput;
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    
    setChatLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          interests
        })
      });

      if (response.ok) {
        const data = await response.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      }
    } catch (error) {
      toast.error('AI chat error');
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="p-6 md:p-8 rounded-b-3xl shadow-lg mb-6" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <Brain className="w-10 h-10 text-white" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white">AI Career Counselor</h1>
          </div>
          <p className="text-lg text-white/90">Your personal AI guide for career exploration! 🚀</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 space-y-6">
        {/* Profile Summary */}
        <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: colors.text }}>Your Profile</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
              <p className="text-sm font-semibold mb-2" style={{ color: colors.text }}>Interests</p>
              <p className="text-2xl font-bold" style={{ color: colors.primary }}>{interests.length}</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
              <p className="text-sm font-semibold mb-2" style={{ color: colors.text }}>Skills Tracked</p>
              <p className="text-2xl font-bold" style={{ color: colors.primary }}>{skills.length}</p>
            </div>
            <div className="p-4 rounded-lg" style={{ backgroundColor: colors.surface }}>
              <p className="text-sm font-semibold mb-2" style={{ color: colors.text }}>Active Goals</p>
              <p className="text-2xl font-bold" style={{ color: colors.primary }}>{goals.filter(g => g.status === 'active').length}</p>
            </div>
          </div>
          
          <Button
            onClick={analyzeProfile}
            disabled={loading || interests.length === 0}
            className="w-full mt-6 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                AI Analyzing Your Profile...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                Get AI Career Recommendations
              </>
            )}
          </Button>
        </Card>

        {/* AI Career Suggestions */}
        {careerSuggestions && careerSuggestions.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-6" style={{ backgroundColor: 'rgba(139,92,246,0.05)', borderColor: '#8B5CF6', borderWidth: '2px' }}>
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-6 h-6" style={{ color: '#8B5CF6' }} />
                <h2 className="text-2xl font-bold" style={{ color: colors.text }}>AI Career Matches</h2>
                <Badge className="bg-purple-500/20 text-purple-600 font-semibold">Powered by GPT-4o</Badge>
              </div>

              <div className="space-y-4">
                {careerSuggestions.map((career, idx) => (
                  <Card key={idx} className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                        💼
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>{career.title}</h3>
                        <p className="text-sm mb-3" style={{ color: colors.text }}>{career.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>Skills Needed:</p>
                            <div className="flex flex-wrap gap-1">
                              {career.skills_needed?.slice(0, 3).map((skill, i) => (
                                <Badge key={i} className="bg-blue-500/10 text-blue-600 font-semibold text-xs">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>Education:</p>
                            <p className="text-xs font-semibold" style={{ color: colors.text }}>{career.education_path}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold" style={{ color: colors.primary }}>
                            💰 {career.salary_range}
                          </p>
                          <Button
                            size="sm"
                            onClick={() => navigate('/goals')}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                          >
                            Set Goal <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* AI Chat Assistant */}
        <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-6 h-6" style={{ color: colors.primary }} />
            <h2 className="text-2xl font-bold" style={{ color: colors.text }}>Ask AI Counselor</h2>
          </div>
          
          <div className="space-y-4 mb-4 max-h-[400px] overflow-y-auto">
            {chatMessages.length === 0 ? (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 mx-auto mb-3" style={{ color: colors.text_secondary }} />
                <p style={{ color: colors.text_secondary }}>Ask me anything about careers, goals, or your future!</p>
              </div>
            ) : (
              chatMessages.map((msg, idx) => (
                <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div 
                    className="max-w-[80%] p-4 rounded-lg"
                    style={{
                      backgroundColor: msg.role === 'user' ? colors.primary : colors.surface,
                      color: msg.role === 'user' ? '#FFFFFF' : colors.text
                    }}
                  >
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex gap-2">
            <Textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Ask: What careers match my interests? What goals should I set?"
              rows={2}
              style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChatMessage();
                }
              }}
            />
            <Button
              onClick={sendChatMessage}
              disabled={chatLoading || !chatInput.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {chatLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            </Button>
          </div>
        </Card>
      </div>

      <Navigation user={user} />
    </div>
  );
};

export default AICareerCounselor;
